import { useEffect, useState, useCallback } from 'react';
import apiCoreAdapter from './apiCoreAdapter';

/** Días off reales pedidos por la psicóloga (ej. 1/9 al 20/9 inclusive). */
export const VACATION_SEGMENT_LABEL = 'Vacaciones & Dias Off';
/** Ventana previa a los días off que la política de asignación bloquea (ver `vacationBlockBracket.ts`). */
export const VACATION_PRE_SEGMENT_LABEL = 'Bloqueo previo a vacaciones';
/** Ventana posterior a los días off que sigue bloqueada al volver (solo brackets > 30 días). */
export const VACATION_POST_SEGMENT_LABEL = 'Bloqueo posterior a vacaciones';
/** Días off reales: se deja de ofrecer la agenda tanto a pacientes nuevos como a los ya activos. */
export const VACATION_TOOLTIP = 'Esto significa que tu agenda no será ofrecida ni a pacientes nuevos ni a pacientes activos durante el período de vacaciones.';
/** Padding previo/posterior: solo afecta la asignación de pacientes nuevos. */
export const VACATION_PADDING_TOOLTIP = 'Aplica solo a pacientes nuevos.';

export type VacationSegmentType = 'vacation' | 'pre' | 'post';

export interface VacationSegment {
  type: VacationSegmentType;
  label: string;
  tooltip: string;
}

interface RawVacationPeriod {
  from: string;
  to?: string;
  duration?: number;
  cancelled?: boolean;
  blockade?: { from: string; to?: string } | null;
}

interface ParsedVacationRange {
  /** Primer/último día off real (YYYY-MM-DD), inclusive. */
  vacFrom: string;
  vacTo: string;
  /** Ventana completa de bloqueo de agenda para pacientes nuevos (padding incluido). */
  blockFrom: string;
  /** null = bloqueo posterior no aplica (sigue igual que `vacTo`) o queda abierto. */
  blockTo: string | null;
}

function toISODateOnly(s: string): string {
  return s.split('T')[0];
}

function addDaysISO(iso: string, days: number): string {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() + days);
  return d.toISOString().split('T')[0];
}

function parseVacationPeriod(v: RawVacationPeriod): ParsedVacationRange {
  const vacFrom = toISODateOnly(v.from);
  const vacTo = v.to
    ? toISODateOnly(v.to)
    : v.duration
      ? addDaysISO(vacFrom, v.duration)
      : vacFrom;
  const blockFrom = v.blockade?.from ? toISODateOnly(v.blockade.from) : vacFrom;
  const blockTo = v.blockade?.to ? toISODateOnly(v.blockade.to) : null;
  return { vacFrom, vacTo, blockFrom, blockTo };
}

/**
 * Fuente única de los períodos de vacaciones/días off de la psicóloga
 * (`apiCoreAdapter.psychologistVacations`), usada por las vistas semanal, diaria y
 * mensual de la agenda para pintar de forma consistente los 3 tramos de bloqueo:
 * el pedido real de días off, y el padding previo/posterior que la política de
 * asignación agrega alrededor (ver `vacationBlockBracket.ts` para la regla de negocio
 * — acá NO se recalcula esa regla, se usa directamente `blockade.from/to` que ya
 * viene computado por el backend, para no desincronizarse si la regla cambia).
 */
export function useVacationBlockade(psychologistId: string | null | undefined) {
  const [ranges, setRanges] = useState<ParsedVacationRange[]>([]);

  useEffect(() => {
    if (!psychologistId) { setRanges([]); return; }
    let cancelled = false;
    apiCoreAdapter.psychologistVacations({ psychologist: psychologistId, skip: 0, take: 100 })
      .then((res) => {
        if (cancelled) return;
        const raw = (Array.isArray(res) ? res : (res as { results?: RawVacationPeriod[] } | undefined)?.results) ?? [];
        setRanges((raw as RawVacationPeriod[]).filter((v) => !v.cancelled).map(parseVacationPeriod));
      })
      .catch(() => { if (!cancelled) setRanges([]); });
    return () => { cancelled = true; };
  }, [psychologistId]);

  const getVacationSegment = useCallback((dateISO: string): VacationSegment | null => {
    for (const r of ranges) {
      if (dateISO >= r.vacFrom && dateISO <= r.vacTo) {
        return { type: 'vacation', label: VACATION_SEGMENT_LABEL, tooltip: VACATION_TOOLTIP };
      }
      if (dateISO >= r.blockFrom && dateISO < r.vacFrom && (!r.blockTo || dateISO <= r.blockTo)) {
        return { type: 'pre', label: VACATION_PRE_SEGMENT_LABEL, tooltip: VACATION_PADDING_TOOLTIP };
      }
      if (r.blockTo && dateISO > r.vacTo && dateISO <= r.blockTo) {
        return { type: 'post', label: VACATION_POST_SEGMENT_LABEL, tooltip: VACATION_PADDING_TOOLTIP };
      }
    }
    return null;
  }, [ranges]);

  const isVacationBlocked = useCallback(
    (dateISO: string): boolean => getVacationSegment(dateISO) !== null,
    [getVacationSegment],
  );

  return { getVacationSegment, isVacationBlocked };
}
